import type { AbilityDef, HazardKind } from '@gra/content';
import { Color } from 'three';
import itemModels from './item-models.json';
import raceModels from './race-models.json';
import armorModels from './armor-models.json';
import type { Grade } from './stage';

/**
 * Mapa kluczy wizualnych z danych gry (class.visual, enemy.visual, item.visual) na pliki modeli.
 * Podmiana grafiki = zmiana ścieżek tutaj, bez ruszania danych ani silnika.
 */
export const MODEL_ROOT = '/models/';

/** Modele bohaterów. Z nich bierzemy też dodatki (broń, tarcze, hełmy) dla item.visual "model:Węzeł". */
/** Oryginalne postacie KayKit: źródło szkieletu, animacji i dodatków (broń, kapelusze) dla ekwipunku. */
export const KAYKIT_HEROES: Record<string, string> = {
  knight: 'kaykit/adventurers/characters/Knight.glb',
  barbarian: 'kaykit/adventurers/characters/Barbarian.glb',
  mage: 'kaykit/adventurers/characters/Mage.glb',
  rogue: 'kaykit/adventurers/characters/Rogue.glb',
};

/**
 * Ciało bohatera w grze. Domyślnie KayKit; postać z AI (npm run asset:character) podmienia tu ścieżkę
 * na models/characters/<id>.glb — ma ten sam szkielet i animacje, więc reszta gry działa bez zmian.
 */
/**
 * Modele ras (postacie z AI na szkielecie o ludzkich proporcjach): "rasa-płeć" → plik.
 * Rasa bez modelu wygląda jak postać klasy (KayKit). Lista w race-models.json.
 */
export const RACE_MODELS: Record<string, string> = raceModels; // dopisuje npm run asset:queue

/**
 * Modele części zbroi na ciało: id przedmiotu → plik, „{g}” to płeć (m/k) — części strojów Quaterniusa
 * (tools/asset-pipeline/src/build-outfits.ts) są osobne dla ciała męskiego i żeńskiego. Bez wpisu — kolor strefy ciała.
 */
export const ARMOR_MODELS: Record<string, string> = armorModels;

/** Plik części zbroi dla płci bohatera. */
export function armorModel(itemId: string, gender: string): string | undefined {
  return ARMOR_MODELS[itemId]?.replace('{g}', gender);
}

/** Wszystkie pliki części zbroi (obie płcie) — do wczytania z góry. */
const ARMOR_PATHS = [...new Set(Object.values(ARMOR_MODELS).flatMap((p) => ['m', 'k'].map((g) => p.replace('{g}', g))))];

/** Gołe ciało bohatera dla wszystkich ras (manekin UAL, Quaternius CC0) — skóra i zbroja w three/body.ts. */
export const BODY_MODEL = 'body:ual';

export const HERO_MODELS: Record<string, string> = {
  ...KAYKIT_HEROES,
  [BODY_MODEL]: 'characters/cialo-ual.glb',
  ...Object.fromEntries(Object.entries(RACE_MODELS).map(([k, v]) => [`race:${k}`, v])),
};

export interface EnemyVisual {
  model: string;
  /** Model trzymany w prawej ręce. */
  weapon?: string;
  /** Model trzymany w lewej ręce. */
  offhand?: string;
  scale: number;
  walk: string;
  /** Cios wręcz, gdy wróg dosięga bohatera. */
  melee: string;
  /** Animacja zamachu przed atakiem specjalnym (strzał, salwa, szarża). */
  cast?: string;
  /** Stała poświata materiału (np. płonąca czaszka, bossowie). */
  tint?: string;
}

export const ENEMY_VISUALS: Record<string, EnemyVisual> = {
  'skeleton-minion': {
    model: 'kaykit/skeletons/characters/Skeleton_Minion.glb',
    weapon: 'kaykit/skeletons/items/Skeleton_Blade.gltf',
    scale: 1,
    walk: 'Walking_D_Skeletons',
    melee: '1H_Melee_Attack_Chop',
  },
  'skeleton-rogue': {
    model: 'kaykit/skeletons/characters/Skeleton_Rogue.glb',
    weapon: 'kaykit/skeletons/items/Skeleton_Axe.gltf',
    scale: 1,
    walk: 'Running_C',
    melee: '1H_Melee_Attack_Slice_Diagonal',
  },
  'skeleton-mage': {
    model: 'kaykit/skeletons/characters/Skeleton_Mage.glb',
    weapon: 'kaykit/skeletons/items/Skeleton_Staff.gltf',
    scale: 0.95,
    walk: 'Running_A',
    melee: '1H_Melee_Attack_Stab',
    cast: 'Spellcast_Shoot',
  },
  'skeleton-brute': {
    model: 'kaykit/skeletons/characters/Skeleton_Warrior.glb',
    weapon: 'kaykit/skeletons/items/Skeleton_Axe.gltf',
    offhand: 'kaykit/skeletons/items/Skeleton_Shield_Large_A.gltf',
    scale: 1.7,
    walk: 'Walking_D_Skeletons',
    melee: '2H_Melee_Attack_Chop',
  },
  'skeleton-archer': {
    model: 'kaykit/skeletons/characters/Skeleton_Rogue.glb',
    weapon: 'kaykit/skeletons/items/Skeleton_Crossbow.gltf',
    scale: 1,
    walk: 'Running_B',
    melee: 'Unarmed_Melee_Attack_Kick',
    cast: '1H_Ranged_Shoot',
  },
  'skeleton-bomber': {
    model: 'kaykit/skeletons/characters/Skeleton_Minion.glb',
    scale: 0.9,
    walk: 'Running_A',
    melee: 'Unarmed_Melee_Attack_Punch_A',
    cast: 'Taunt',
    tint: '#5a1800',
  },
  'skeleton-charger': {
    model: 'kaykit/skeletons/characters/Skeleton_Warrior.glb',
    weapon: 'kaykit/skeletons/items/Skeleton_Blade.gltf',
    offhand: 'kaykit/skeletons/items/Skeleton_Shield_Large_A.gltf',
    scale: 1.2,
    walk: 'Walking_D_Skeletons',
    melee: 'Block_Attack',
    cast: 'Taunt',
  },
  'skeleton-frost': {
    model: 'kaykit/skeletons/characters/Skeleton_Warrior.glb',
    weapon: 'kaykit/skeletons/items/Skeleton_Axe.gltf',
    offhand: 'kaykit/skeletons/items/Skeleton_Shield_Small_A.gltf',
    scale: 1.25,
    walk: 'Walking_D_Skeletons',
    melee: '1H_Melee_Attack_Chop',
    tint: '#0b3350',
  },
  'skeleton-frost-mage': {
    model: 'kaykit/skeletons/characters/Skeleton_Mage.glb',
    weapon: 'kaykit/skeletons/items/Skeleton_Staff.gltf',
    scale: 1,
    walk: 'Walking_D_Skeletons',
    melee: '1H_Melee_Attack_Stab',
    cast: 'Spellcast_Shoot',
    tint: '#08405e',
  },
  'boss-frost': {
    model: 'kaykit/skeletons/characters/Skeleton_Warrior.glb',
    weapon: 'kaykit/skeletons/items/Skeleton_Axe.gltf',
    offhand: 'kaykit/skeletons/items/Skeleton_Shield_Large_A.gltf',
    scale: 3,
    walk: 'Walking_D_Skeletons',
    melee: '2H_Melee_Attack_Chop',
    cast: '1H_Melee_Attack_Jump_Chop',
    tint: '#0a3a66',
  },
  'boss-king': {
    model: 'kaykit/skeletons/characters/Skeleton_Warrior.glb',
    weapon: 'kaykit/skeletons/items/Skeleton_Axe.gltf',
    offhand: 'kaykit/skeletons/items/Skeleton_Shield_Large_A.gltf',
    scale: 2.9,
    walk: 'Walking_D_Skeletons',
    melee: '2H_Melee_Attack_Chop',
    cast: '1H_Melee_Attack_Jump_Chop',
    tint: '#2a1a00',
  },
  'boss-necro': {
    model: 'kaykit/skeletons/characters/Skeleton_Mage.glb',
    weapon: 'kaykit/skeletons/items/Skeleton_Staff.gltf',
    scale: 2.6,
    walk: 'Walking_D_Skeletons',
    melee: '1H_Melee_Attack_Stab',
    cast: 'Spellcast_Summon',
    tint: '#1e0a36',
  },
};

/**
 * Animacja ataku bohatera wg efektu umiejętności (ability.fx) i broni.
 * `alt` przełącza warianty, żeby kolejne ciosy nie wyglądały identycznie.
 */
export function attackAnimation(fx: string, twoHanded: boolean, alt: boolean): string | null {
  switch (fx) {
    case 'slash':
      if (twoHanded) return '2H_Melee_Attack_Slice';
      return alt ? '1H_Melee_Attack_Slice_Diagonal' : '1H_Melee_Attack_Slice_Horizontal';
    case 'cleave':
      return twoHanded ? '2H_Melee_Attack_Chop' : '1H_Melee_Attack_Chop';
    case 'spin':
      // Obrót z wyciągniętą bronią: animacja całego ciała z modelu (biodra prowadzą, broń zatacza koło).
      return '2H_Melee_Attack_Spinning';
    case 'bolt':
      return twoHanded ? '2H_Ranged_Shoot' : '1H_Ranged_Shoot';
    case 'knife':
    case 'totem':
      return 'Throw';
    case 'flame':
      return 'Spellcast_Raise';
    default:
      return 'Spellcast_Shoot'; // orb, shard
  }
}

/**
 * Kolor efektu umiejętności na danej randze: z każdym ulepszeniem przechodzi od `color` do `rankColor`.
 * Formy po ewolucji mają już własny, docelowy kolor.
 */
export function abilityColor(def: AbilityDef, rank: number, maxRank: number): string {
  if (!def.rankColor || maxRank <= 1) return def.color;
  const k = Math.min(1, Math.max(0, (rank - 1) / (maxRank - 1)));
  return '#' + new Color(def.color).lerp(new Color(def.rankColor), k).getHexString();
}

/** Dodatkowe modele, z których bierzemy dodatki (kaptury, kapelusze) dla item.visual "klucz:Węzeł". */
export const ACCESSORY_SOURCES: Record<string, string> = {
  ...KAYKIT_HEROES,
  'skeleton-rogue': 'kaykit/skeletons/characters/Skeleton_Rogue.glb',
  'skeleton-mage': 'kaykit/skeletons/characters/Skeleton_Mage.glb',
};

export interface PropDef {
  path: string;
  scale: number;
  /** Waga losowania (im większa, tym częściej). */
  weight: number;
  /** Duże obiekty rzucają cień, drobnica nie. */
  shadow?: boolean;
  /**
   * Wielkość przeszkody, którą ten model może udawać (0 mała, 1 średnia, 2 duża).
   * Bez tego pola model jest drobnicą: stoi luzem i da się przez niego przejść.
   */
  block?: 0 | 1 | 2;
  /** Świecąca dekoracja: kolor, wysokość płomienia nad ziemią (przy skali 1) i siła światła. */
  glow?: { color: string; height: number; intensity: number };
}

const H = 'kaykit/halloween/';
const D = 'kaykit/dungeon/';
const X = 'kaykit/hexagon/';

/**
 * Modele, które mają zostać kanciaste (skrzynki, ściany, płoty, broń): zaokrąglone wyglądałyby jak roztopione.
 * Reszta dekoracji jest wygładzana przy wczytywaniu.
 */
export const KEEP_SHARP = ['crate', 'wall_', 'chest', 'column', 'sword_shield', 'fence', 'weaponrack', 'flag', 'bucket', 'tent', 'building_', 'arch', 'crypt', 'pillar', 'shrine', 'torch', 'post_skull', 'grave_', 'gravestone', 'gravemarker'];

export function isSharp(path: string): boolean {
  return KEEP_SHARP.some((k) => path.includes(k));
}

/** Dekoracje mapy wyprawy: stary cmentarz i ruiny. */
export const RUN_PROPS: PropDef[] = [
  { path: H + 'grave_A.gltf', scale: 0.9, weight: 3, shadow: true, block: 1 },
  { path: H + 'grave_A_destroyed.gltf', scale: 0.9, weight: 3, shadow: true, block: 1 },
  { path: H + 'grave_B.gltf', scale: 0.9, weight: 2, shadow: true, block: 1 },
  { path: H + 'gravemarker_A.gltf', scale: 1, weight: 4, shadow: true, block: 0 },
  { path: H + 'gravemarker_B.gltf', scale: 1, weight: 4, shadow: true, block: 0 },
  { path: H + 'gravestone.gltf', scale: 0.9, weight: 3, shadow: true, block: 0 },
  { path: H + 'bone_A.gltf', scale: 0.8, weight: 5 },
  { path: H + 'bone_B.gltf', scale: 0.8, weight: 5 },
  { path: H + 'bone_C.gltf', scale: 0.8, weight: 4 },
  { path: H + 'ribcage.gltf', scale: 0.9, weight: 3 },
  { path: H + 'skull.gltf', scale: 0.55, weight: 3 },
  { path: H + 'skull_candle.gltf', scale: 0.6, weight: 1, glow: { color: '#ffb050', height: 0.7, intensity: 5 } },
  { path: H + 'tree_dead_large.gltf', scale: 1, weight: 1.2, shadow: true, block: 1 },
  { path: H + 'tree_dead_medium.gltf', scale: 1, weight: 1.5, shadow: true, block: 0 },
  { path: H + 'tree_dead_small.gltf', scale: 1, weight: 2, shadow: true, block: 0 },
  { path: H + 'tree_pine_orange_medium.gltf', scale: 0.9, weight: 0.6, shadow: true, block: 1 },
  { path: H + 'tree_pine_yellow_medium.gltf', scale: 0.9, weight: 0.4, shadow: true, block: 1 },
  { path: H + 'fence_broken.gltf', scale: 0.8, weight: 1, shadow: true },
  { path: H + 'fence_pillar_broken.gltf', scale: 0.9, weight: 1, shadow: true, block: 0 },
  { path: H + 'post_skull.gltf', scale: 0.7, weight: 0.6, shadow: true, block: 0 },
  { path: H + 'lantern_standing.gltf', scale: 1, weight: 1, glow: { color: '#ffc070', height: 1.7, intensity: 8 } },
  { path: H + 'pumpkin_orange.gltf', scale: 0.7, weight: 1 },
  { path: H + 'pumpkin_orange_jackolantern.gltf', scale: 0.6, weight: 0.5, glow: { color: '#ff9a30', height: 0.6, intensity: 5 } },
  { path: H + 'crypt.gltf', scale: 0.8, weight: 0.12, shadow: true, block: 2 },
  { path: H + 'shrine_candles.gltf', scale: 0.9, weight: 0.4, shadow: true, block: 0, glow: { color: '#ffb050', height: 0.9, intensity: 7 } },
  { path: H + 'pillar.gltf', scale: 0.7, weight: 0.4, shadow: true, block: 0 },
  { path: H + 'arch.gltf', scale: 0.8, weight: 0.2, shadow: true },
  { path: D + 'rubble_half.glb', scale: 0.6, weight: 0.4, shadow: true, block: 2 },
  { path: D + 'rubble_large.glb', scale: 0.6, weight: 0.15, shadow: true, block: 2 },
  { path: D + 'barrel_large.glb', scale: 0.55, weight: 0.6, shadow: true, block: 0 },
  { path: D + 'crates_stacked.glb', scale: 0.6, weight: 0.4, shadow: true, block: 1 },
  { path: D + 'sword_shield_broken.glb', scale: 0.7, weight: 0.6 },
  { path: D + 'column.glb', scale: 0.9, weight: 0.5, shadow: true, block: 0 },
  { path: D + 'wall_broken.glb', scale: 0.8, weight: 0.3, shadow: true, block: 2 },
  { path: D + 'torch_lit.glb', scale: 0.9, weight: 0.5, glow: { color: '#ff8a30', height: 1.5, intensity: 9 } },
  { path: D + 'chest_gold.glb', scale: 0.6, weight: 0.1, shadow: true, block: 0 },
];

/** Dekoracje jesiennego lasu: drzewa, skały, porzucone obozowiska. */
export const FOREST_PROPS: PropDef[] = [
  { path: X + 'tree_single_A.gltf', scale: 3.6, weight: 3, shadow: true, block: 1 },
  { path: X + 'tree_single_B.gltf', scale: 3.6, weight: 3, shadow: true, block: 1 },
  { path: X + 'trees_A_medium.gltf', scale: 3.6, weight: 0.6, shadow: true, block: 2 },
  { path: H + 'tree_pine_orange_medium.gltf', scale: 0.9, weight: 2, shadow: true, block: 1 },
  { path: H + 'tree_pine_yellow_medium.gltf', scale: 0.9, weight: 2, shadow: true, block: 1 },
  { path: H + 'tree_pine_orange_large.gltf', scale: 0.8, weight: 0.8, shadow: true, block: 2 },
  { path: H + 'tree_dead_small.gltf', scale: 1, weight: 0.6, shadow: true, block: 0 },
  { path: X + 'rock_single_A.gltf', scale: 6, weight: 2, shadow: true, block: 1 },
  { path: X + 'rock_single_B.gltf', scale: 6, weight: 2, shadow: true, block: 1 },
  { path: X + 'rock_single_C.gltf', scale: 6, weight: 1.5, shadow: true, block: 1 },
  { path: X + 'rock_single_D.gltf', scale: 6, weight: 1.5, shadow: true, block: 1 },
  { path: H + 'pumpkin_orange.gltf', scale: 0.7, weight: 1 },
  { path: H + 'fence_broken.gltf', scale: 0.8, weight: 0.5, shadow: true },
  { path: H + 'lantern_standing.gltf', scale: 1, weight: 0.5, glow: { color: '#ffc070', height: 1.7, intensity: 8 } },
  { path: D + 'barrel_large.glb', scale: 0.55, weight: 0.4, shadow: true, block: 0 },
  { path: D + 'crates_stacked.glb', scale: 0.6, weight: 0.3, shadow: true, block: 1 },
  { path: D + 'sword_shield_broken.glb', scale: 0.7, weight: 0.4 },
  { path: X + 'resource_lumber.gltf', scale: 5, weight: 0.4, shadow: true, block: 1 },
  { path: X + 'tent.gltf', scale: 6, weight: 0.15, shadow: true, block: 2 },
];

/** Dekoracje zamarzniętych ruin: kolumny, mury, gruz, martwe drzewa i skały. */
export const RUINS_PROPS: PropDef[] = [
  { path: D + 'column.glb', scale: 0.9, weight: 2.5, shadow: true, block: 0 },
  { path: H + 'pillar.gltf', scale: 0.8, weight: 2, shadow: true, block: 0 },
  { path: D + 'wall_broken.glb', scale: 0.85, weight: 1.6, shadow: true, block: 2 },
  { path: D + 'rubble_half.glb', scale: 0.65, weight: 1.4, shadow: true, block: 2 },
  { path: D + 'rubble_large.glb', scale: 0.6, weight: 0.8, shadow: true, block: 2 },
  { path: H + 'crypt.gltf', scale: 0.8, weight: 0.25, shadow: true, block: 2 },
  { path: X + 'rock_single_A.gltf', scale: 6, weight: 1.5, shadow: true, block: 1 },
  { path: X + 'rock_single_C.gltf', scale: 6, weight: 1.2, shadow: true, block: 1 },
  { path: H + 'tree_dead_large.gltf', scale: 1, weight: 1, shadow: true, block: 1 },
  { path: H + 'tree_dead_medium.gltf', scale: 1, weight: 1, shadow: true, block: 0 },
  { path: H + 'arch.gltf', scale: 0.8, weight: 0.4, shadow: true },
  { path: D + 'sword_shield_broken.glb', scale: 0.7, weight: 1 },
  { path: H + 'skull.gltf', scale: 0.55, weight: 1.5 },
  { path: H + 'bone_A.gltf', scale: 0.8, weight: 2 },
  { path: H + 'ribcage.gltf', scale: 0.9, weight: 1 },
  { path: H + 'fence_pillar_broken.gltf', scale: 0.9, weight: 0.8, shadow: true, block: 0 },
  { path: D + 'torch_lit.glb', scale: 0.9, weight: 0.5, glow: { color: '#7fc8ff', height: 1.5, intensity: 8 } },
  { path: H + 'shrine_candles.gltf', scale: 0.9, weight: 0.3, shadow: true, block: 0, glow: { color: '#9fd8ff', height: 0.9, intensity: 6 } },
];

/** Wygląd strefy: teren, niebo, światło, kolory postaci i korekcja obrazu. */
export interface ZoneLook {
  ground: { grass: string; grassDark: string; dirt: string; stone: string; dirtAmount: number; stoneAmount: number };
  blades: string;
  rocks: string;
  sky: { zenith: string; horizon: string; clouds: number };
  fog: [number, number];
  sun: { color: string; intensity: number };
  fill: { sky: string; ground: string; intensity: number };
  /** Światło z tyłu sceny — chłodny kontur postaci i obiektów. */
  back: { color: string; intensity: number };
  rim: { color: string; strength: number };
  /** Kolor unoszących się w powietrzu drobin (świetliki, pyłki). */
  motes: string;
  /** Klimat w powietrzu: spadające liście, mgła przy ziemi, śnieg albo nic. */
  ambience?: 'leaves' | 'snow';
  grade: Grade;
  props: PropDef[];
  /** Modele obiektów do rozbicia (beczki, skrzynki); wybór po `variant` z silnika. */
  breakables?: BreakableProp[];
}

export interface BreakableProp {
  path: string;
  scale: number;
  /** Kolor drzazg przy rozbiciu. */
  chips: string;
}

/** Kolory kałuż: plama, jaśniejszy brzeg i drobiny unoszące się nad nią. */
export const HAZARD_LOOKS: Record<HazardKind, { color: string; edge: string; particles: string; glow: number }> = {
  trucizna: { color: '#22401a', edge: '#5a8a30', particles: '#9ad450', glow: 0.12 },
  lod: { color: '#5f9cc4', edge: '#d8f2ff', particles: '#e8f8ff', glow: 0.45 },
  zar: { color: '#7a1f08', edge: '#ff8a2a', particles: '#ffb050', glow: 1.2 },
};

const BARRELS: BreakableProp[] = [
  { path: 'kaykit/dungeon/barrel_large.glb', scale: 0.42, chips: '#8a5a32' },
  { path: 'kaykit/hexagon/crate_A_big.gltf', scale: 3.2, chips: '#a87a48' },
];

export const ZONE_LOOKS: Record<string, ZoneLook> = {
  cmentarz: {
    ground: { grass: '#4d6a33', grassDark: '#2c4322', dirt: '#6b5236', stone: '#7a7468', dirtAmount: 0.45, stoneAmount: 0.6 },
    blades: '#6e8f3e',
    rocks: '#857f74',
    sky: { zenith: '#26355a', horizon: '#d08a5c', clouds: 0.5 },
    fog: [24, 52],
    sun: { color: '#ffb36b', intensity: 2.7 },
    fill: { sky: '#9fb4e0', ground: '#3a2c22', intensity: 0.9 },
    back: { color: '#6f8cff', intensity: 1.2 },
    rim: { color: '#ffcf99', strength: 0.55 },
    motes: '#ffd27a',
    // Bez kłębów mgły: rodziły się wokół bohatera i przy brzegach ekranu wyglądały jak brud na obiektywie.
    grade: { saturation: 1.18, contrast: 1.08, warmth: 0.7, vignette: 0.42, bloom: 0.5 },
    props: RUN_PROPS,
    breakables: [...BARRELS, { path: H + 'pumpkin_orange.gltf', scale: 0.75, chips: '#e8812a' }],
  },
  las: {
    ground: { grass: '#4c6e2c', grassDark: '#2b4519', dirt: '#7a5a38', stone: '#8a8a80', dirtAmount: 0.4, stoneAmount: 0.5 },
    blades: '#6f9a3c',
    rocks: '#8f8c84',
    sky: { zenith: '#4b8bd4', horizon: '#d6eaf2', clouds: 0.7 },
    fog: [28, 58],
    sun: { color: '#ffe2b0', intensity: 2.5 },
    fill: { sky: '#bfe0ff', ground: '#40351f', intensity: 1.1 },
    back: { color: '#a0c8ff', intensity: 0.8 },
    rim: { color: '#fff3d0', strength: 0.45 },
    motes: '#f0ffb8',
    ambience: 'leaves',
    grade: { saturation: 1.08, contrast: 1.06, warmth: 0.55, vignette: 0.35, bloom: 0.4 },
    props: FOREST_PROPS,
    breakables: [...BARRELS, { path: X + 'sack.gltf', scale: 4, chips: '#c9a86a' }],
  },
  ruiny: {
    ground: { grass: '#b4c4cf', grassDark: '#7f93a6', dirt: '#6c7a88', stone: '#9aa6b2', dirtAmount: 0.35, stoneAmount: 0.75 },
    blades: '#b8cfdc',
    rocks: '#a6b2bf',
    sky: { zenith: '#1d2c4a', horizon: '#9fb8d6', clouds: 0.8 },
    fog: [20, 46],
    sun: { color: '#d8e8ff', intensity: 2.2 },
    fill: { sky: '#b8d4ff', ground: '#3a4250', intensity: 1.2 },
    back: { color: '#7fb4ff', intensity: 1.6 },
    rim: { color: '#cfe6ff', strength: 0.6 },
    motes: '#e6f4ff',
    ambience: 'snow',
    grade: { saturation: 0.92, contrast: 1.1, warmth: 0.15, vignette: 0.45, bloom: 0.55 },
    props: RUINS_PROPS,
    breakables: BARRELS,
  },
  oboz: {
    ground: { grass: '#557e31', grassDark: '#304d1e', dirt: '#7a5a38', stone: '#8a8273', dirtAmount: 0.55, stoneAmount: 0.3 },
    blades: '#7aa944',
    rocks: '#8a857a',
    sky: { zenith: '#2f4a7a', horizon: '#f0b27a', clouds: 0.6 },
    fog: [12, 34],
    sun: { color: '#ffc47e', intensity: 2 },
    fill: { sky: '#b0c4ef', ground: '#3a2c22', intensity: 1 },
    back: { color: '#7d9cff', intensity: 1.4 },
    rim: { color: '#ffd7a3', strength: 0.7 },
    motes: '#ffcf6a',
    grade: { saturation: 1.15, contrast: 1.07, warmth: 0.8, vignette: 0.5, bloom: 0.55 },
    props: [],
  },
};

export interface PlacedProp {
  path: string;
  scale: number;
  x: number;
  z: number;
  /** Obrót wokół osi pionowej w stopniach. */
  rot: number;
}

/** Obóz: rozstawienie wokół podestu bohatera (kamera patrzy od strony +z). */
export const CAMP_PROPS: PlacedProp[] = [
  { path: X + 'tent.gltf', scale: 7, x: -3.6, z: -3.6, rot: 30 },
  { path: X + 'weaponrack.gltf', scale: 7, x: 2.7, z: -2.4, rot: -25 },
  { path: X + 'target.gltf', scale: 7, x: 4.6, z: -4.4, rot: -35 },
  { path: X + 'barrel.gltf', scale: 7, x: -2.5, z: -1.3, rot: 0 },
  { path: X + 'crate_A_big.gltf', scale: 7, x: -3.3, z: -0.6, rot: 20 },
  { path: X + 'sack.gltf', scale: 7, x: -2.4, z: -0.3, rot: 60 },
  { path: X + 'bucket_arrows.gltf', scale: 7, x: 3.4, z: -1.6, rot: 0 },
  { path: X + 'flag_red.gltf', scale: 8, x: -1.2, z: -4.8, rot: 70 },
  { path: X + 'resource_lumber.gltf', scale: 6, x: 5.2, z: -1.2, rot: 15 },
  { path: X + 'building_well_blue.gltf', scale: 5, x: -6.5, z: -6.5, rot: 20 },
  { path: X + 'trees_A_large.gltf', scale: 6, x: 7.5, z: -9, rot: 0 },
  { path: X + 'trees_A_medium.gltf', scale: 6, x: -9.5, z: -8, rot: 40 },
  { path: X + 'tree_single_A.gltf', scale: 5, x: -6.5, z: -1.5, rot: 0 },
  { path: X + 'tree_single_B.gltf', scale: 5, x: 7.5, z: -3.5, rot: 0 },
  { path: X + 'rock_single_A.gltf', scale: 6, x: 2, z: 1.2, rot: 30 },
  { path: X + 'rock_single_C.gltf', scale: 6, x: -4.5, z: 1.5, rot: 0 },
  { path: X + 'rock_single_D.gltf', scale: 6, x: 4.8, z: 0.8, rot: 80 },
  { path: D + 'torch_lit.glb', scale: 1, x: 1.9, z: -0.9, rot: 0 },
  { path: D + 'torch_lit.glb', scale: 1, x: -1.9, z: -0.9, rot: 0 },
];

/** Jednostki świata silnika → jednostki sceny 3D (postać ma ok. 2,2 jednostki wysokości). */
export const WORLD_SCALE = 1 / 24;

/**
 * Dopasowanie osobnego modelu przedmiotu (item.visual "item:id") do kości postaci.
 * Plik generuje `npm run asset:import`; rotation/offset można potem ręcznie poprawić.
 */
export interface ItemModelFit {
  file: string;
  /** Kość: handslotr, handslotl, head, chest. */
  bone: string;
  /** Skala sprowadzająca model do rozmiaru przedmiotów w grze. */
  scale: number;
  /** Punkt chwytu/zaczepienia w układzie modelu — ląduje dokładnie na kości. */
  anchor: [number, number, number];
  /** Obrót w stopniach (x, y, z) względem kości. */
  rotation: [number, number, number];
  /** Dodatkowe przesunięcie względem kości. */
  offset: [number, number, number];
}

/** JSON daje tablice zwykłych liczb, stąd rzutowanie przez unknown; poprawność pilnuje skrypt importu. */
export const ITEM_MODELS = itemModels as unknown as Record<string, ItemModelFit>;

/** Modele pierwszego ekranu (obóz): ciała bohaterów, źródła dodatków, przedmioty i dekoracje obozu. */
export function hubModelPaths(): string[] {
  const items = Object.values(ITEM_MODELS).map((m) => m.file);
  return [...new Set([...Object.values(ACCESSORY_SOURCES), ...Object.values(HERO_MODELS), ...ARMOR_PATHS, ...items, ...CAMP_PROPS.map((p) => p.path)])];
}

export function allModelPaths(): string[] {
  const enemy = Object.values(ENEMY_VISUALS).flatMap((v) => [v.model, v.weapon, v.offhand]);
  const items = Object.values(ITEM_MODELS).map((m) => m.file);
  const props = [...RUN_PROPS, ...FOREST_PROPS, ...RUINS_PROPS, ...CAMP_PROPS, ...Object.values(ZONE_LOOKS).flatMap((z) => z.breakables ?? [])].map((p) => p.path);
  return [...new Set([...Object.values(ACCESSORY_SOURCES), ...Object.values(HERO_MODELS), ...ARMOR_PATHS, ...enemy, ...items, ...props].filter((p): p is string => !!p))];
}
